import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import {  SortOrder } from "antd/es/table/interface";
import { IAppUser, IGiveawayWithDetails } from "../../../../interfaces";

import useAuth from "../../../../hooks/useAuth";
import { formatDateStringToDate } from "../../../../utils/Conversores";
import GiveawaysService from "../../../../services/http/services/GiveawaysService";
import AppUsersService from "../../../../services/http/services/AppUsersService";
import { EmitError } from "../../../../utils/EmitError";
import { formatCurrency } from "../../../../utils/Masks";
import useModules from "../../../../hooks/useModules";
import { useGetColumnSearchProps } from "../../../../hooks/FilterTable/stringClearFiltros";
import { useGetColumnSearchPropsData } from "../../../../hooks/FilterTable/date";

interface ITableData extends IGiveawayWithDetails {
    key: number | undefined;
    total: number,
}

export default function useTicketsGroupedByGiveaway() {
    const [appUser, setAppUser] = useState<IAppUser>();
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const refFuncaoLimparFiltrosString = useRef<any>(null);

    const navigate = useNavigate();
    const { activeCity } = useAuth();
    const { hasAtLeastOneInvoiceModule, hasTaxesModule } = useModules();
    const { id } = useParams();

    const columns = [
        {
            title: 'Descrição',
            dataIndex: 'description',
            key: 'description',
            sorter: (a: ITableData, b: ITableData) => a.description.localeCompare(b.description),
            showSorterTooltip: false,
            width: '25%',
            ellipsis: true,
            ...useGetColumnSearchProps({dataIndex: 'description', ref: refFuncaoLimparFiltrosString}),
        },
        {
            title: 'Início',
            dataIndex: 'period_beginning',
            key: 'period_beginning',
            sorter: (a: ITableData, b: ITableData) => new Date(a.period_beginning).getTime() - new Date(b.period_beginning).getTime(),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({dataIndex: 'period_beginning'}),
            render: (data: any) => (
                data ? formatDateStringToDate(data) : ''
            ),
        },
        {
            title: 'Fim',
            dataIndex: 'period_ending',
            key: 'period_ending',
            sorter: (a: ITableData, b: ITableData) => new Date(a.period_ending).getTime() - new Date(b.period_ending).getTime(),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({dataIndex: 'period_ending'}),
            render: (data: any) => (
                data ? formatDateStringToDate(data) : ''
            ),
        },
        {
            title: 'Data do sorteio',
            dataIndex: 'date',
            key: 'date',
            defaultSortOrder: 'descend' as keyof SortOrder,
            sorter: (a: ITableData, b: ITableData) => new Date(a.date).getTime() - new Date(b.date).getTime(),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({dataIndex: 'date'}),
            render: (data: any) => (
                data ? formatDateStringToDate(data) : ''
            ),
        },
        {
            title: 'Notas',
            dataIndex: 'total_invoices_balance',
            key: 'total_invoices_balance',
            render: (record: number) => record ? `R$ ${formatCurrency(record)}` : 'R$ 0,00',
            sorter: (a: ITableData, b: ITableData) => a.total_invoices_balance - b.total_invoices_balance,
            showSorterTooltip: false,
            hidden: !hasAtLeastOneInvoiceModule,
        },
        {
            title: 'Impostos',
            dataIndex: 'total_taxes_balance',
            key: 'total_taxes_balance',
            render: (record: number) => record ? `R$ ${formatCurrency(record)}` : 'R$ 0,00',
            sorter: (a: ITableData, b: ITableData) => a.total_taxes_balance - b.total_taxes_balance,
            showSorterTooltip: false,
            hidden: !hasTaxesModule,
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            render: (record: number) => record ? `R$ ${formatCurrency(record)}` : 'R$ 0,00',
            sorter: (a: ITableData, b: ITableData) => a.total - b.total,
            showSorterTooltip: false,
            hidden: hasAtLeastOneInvoiceModule !== hasTaxesModule || (!hasAtLeastOneInvoiceModule && !hasTaxesModule)
        },
        {
            title: 'Bilhetes',
            dataIndex: 'total_tickets',
            key: 'total_tickets',
            render: (record: number) => record.toLocaleString('pt-BR'),
            sorter: (a: ITableData, b: ITableData) => a.total_tickets - b.total_tickets,
            showSorterTooltip: false,
        },
    ].filter((column) => !column?.hidden);

    const onClickTableRow = useCallback((record: any, index: any) => {
        if (selectedIds.includes(record.id)) {
            setSelectedIds((prevState) => prevState.filter((id) => id !== record.id));
        } else {
            setSelectedIds((prevState) => [...prevState, record.id]);
        }
    }, [selectedIds]);

    const onSelectChange = useCallback((newSelectedRowKeys: Array<any>) => {
        if (newSelectedRowKeys.length > selectedIds.length) {
            if (newSelectedRowKeys.length - 1 === selectedIds.length) {
                const newKey = newSelectedRowKeys.pop();
                return setSelectedIds((prevState) => [...prevState, newKey]);
            }

            if (newSelectedRowKeys.length - 1 > selectedIds.length) {
                return setSelectedIds(newSelectedRowKeys);
            }
        }

        setSelectedIds(newSelectedRowKeys);
    }, [selectedIds]);

    const rowSelection = useMemo(() => ({
        type: 'checkbox' as const,
        selectedRowKeys: selectedIds,
        onChange: onSelectChange,
    }), [selectedIds, onSelectChange]);

    const setTableDataFromRequest = useCallback((data: Array<IGiveawayWithDetails>) => {
        const newData = data.map((giveaway) => ({
            ...giveaway,
            key: giveaway.id,
            total: giveaway.total_invoices_balance + giveaway.total_taxes_balance,
        }));
        setTableData(newData);
    }, []);

    const handleClickGoBack = useCallback(() => {
        navigate('/usuarios/aplicativo');
    }, [navigate]);

    const handleClickViewTickets = useCallback(() => {
        navigate(`/usuarios/aplicativo/${id}/bilhetes/sorteio/${selectedIds[0]}`);
    }, [navigate, id, selectedIds]);

    useEffect(() => {

        const controller = new AbortController();

        async function loadData() {
            try {
                setIsLoading(true);

                const cityId = activeCity?.id!;
                const appUserId = Number(id);
                const signal = controller.signal;

                const [giveawaysList, appUserData] = await Promise.all([
                    GiveawaysService.listGiveawaysWithDetails({cityId, appUserId, signal}),
                    AppUsersService.getAppUserById({appUserId, signal}),
                ]);

                setTableDataFromRequest(giveawaysList);
                setAppUser(appUserData);
                setIsLoading(false);

            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'viewAppUserTickets_asodkaskldasd');
                setIsLoading(false);
                navigate('/usuarios/aplicativo');
            }
        }

        loadData();

        return () => {
            controller.abort();
        }
    }, [id, activeCity, navigate, setTableDataFromRequest]);

    return {
        columns,
        tableData,
        appUser,
        handleClickGoBack,
        isLoading,
        rowSelection,
        onClickTableRow,
        selectedIds,
        handleClickViewTickets,
    }
}
